import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Location } from '@angular/common';
import { Router, ActivatedRoute, Params } from '@angular/router';
import {
  InspectionModelService,
  InspectionPluginService,
  InspectionSectionService,
  LanguageService
} from '../../shared/services/index';
import { Inspection, InspectionSection } from '../../shared/models/index';
import { InspectionDetailMenu } from '../../menus/inspection.menu';
import { InspectionModelMenuComponent } from '../menus/inspection-model.menu';
import { BasePageComponent } from '../../base-page.component';
import { ConfirmComponent } from '../../modal/confirm.component';
import { OrderSummariesComponent } from '../../utils/component/order-summaries.component';
import { ApplicationType } from '../../utils';

import * as _ from 'lodash';

@Component({
    templateUrl: './detail.component.html',
    standalone: false
})
export class InspectionModelDetailPageComponent extends BasePageComponent implements OnInit {
  inspection: Inspection;
  errorMessage: string;
  inspectionId: string;
  @ViewChild('fileInput', { static: true })
  fileInput: ElementRef;
  @ViewChild('translationInput', { static: true })
  translationInput: ElementRef;
  @ViewChild('deleteConfirm', { static: true })
  deleteConfirm: ConfirmComponent;
  @ViewChild('sendToValidateConfirm', { static: true })
  sendToValidateConfirm: ConfirmComponent;
  @ViewChild('orderSections', { static: true }) orderSectionsModal: OrderSummariesComponent;
  @ViewChild('orderPlugins', { static: true }) orderPluginsModal: OrderSummariesComponent;
  @ViewChild('sectionsDeleteConfirm', { static: true }) sectionsDeleteConfirm: ConfirmComponent;
  plugins: any[];
  selectedItems: { id: string; name: string }[] = [];

  constructor(
    private inspectionModelService: InspectionModelService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private language: LanguageService,
    private sectionService: InspectionSectionService,
    private pluginsService: InspectionPluginService,
    location: Location
  ) {
    super(location);
  }

  ngOnInit(): void {
    this.initRoute();
  }

  initRoute(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const id = params['inspectionId'];
      if (id == null) this.goToBack();

      this.inspectionId = id;
      this.initData();
    });
  }

  openSection(section: InspectionSection): void {
    this.router.navigate([this.templatesRoute, this.inspection.id, 'sections', section.id]);
  }

  selectSection(section: any): void {
    section.selected = !section.selected;
    if (section.selected) {
      this.selectedItems.push({ id: section.id, name: section.name });
    } else {
      this.selectedItems = this.selectedItems.filter((s) => s.id !== section.id);
    }
  }

  initData(): void {
    this.inspectionModelService.getModel(this.inspectionId).subscribe((el) => {
      this.inspection = el;
      this.selectedItems.push(
        ...this.inspection.sections
          .filter((p: any) => p.selected)
          .map((p: InspectionSection) => ({ id: p.id, name: p.name }))
      );
    });
  }

  public openMenu(): void {
    this.modal.modalFooter = false;
    this.modal.modalHeader = false;
    this.modal.modalMessage = false;
    this.modal.open(InspectionModelMenuComponent, {
      inspection: this.inspection,
      currentPerson: this.currentPerson,
      anyModules: !!this.selectedItems.length
    });
  }

  public selectMenu(item: any) {
    switch (item) {
      case InspectionDetailMenu.Information:
        this.gotoInformationPage();
        break;
      case InspectionDetailMenu.Properties:
        this.gotoEditPage();
        break;
      case InspectionDetailMenu.ImportModule:
        this.gotoAddSectionPage();
        break;
      case InspectionDetailMenu.AddPlugin:
        this.gotoAddPluginPage();
        break;
      case InspectionDetailMenu.CreateModule:
        this.gotoCreateSectionPage();
        break;
      case InspectionDetailMenu.ImportParts:
        this.openFileSelector();
        break;
      case InspectionDetailMenu.Delete:
        this.openDeleteConfirm();
        break;
      case InspectionDetailMenu.OrderModules:
        this.openOrderSectionsPage();
        break;
      case InspectionDetailMenu.AssociateMachines:
        this.gotoAssociatesMachinePage();
        break;
      case InspectionDetailMenu.Status:
        this.gotoUpdateStatusPage();
        break;
      case InspectionDetailMenu.OrderPlugins:
        this.openOrderPluginsPage();
        break;
      case InspectionDetailMenu.DownloadTranslation:
        this.downloadTranslation();
        break;
      case InspectionDetailMenu.DeleteSelectedModules:
        this.openSectionsDeleteConfirm();
        break;
      case InspectionDetailMenu.ImportTranslation:
        this.openTranslationSelector();
        break;
      default:
    }
  }

  openSectionsDeleteConfirm() {
    if (this.selectedItems.length > 0) {
      this.sectionsDeleteConfirm.open();
    }
  }

  public deleteSections(e: any) {
    this.sectionService
      .deleteSections(
        this.inspectionId,
        this.inspection.sections.filter((p: any) => p.selected).map((x) => x.id)
      )
      .subscribe(() => {
        this.initData();
        this.selectedItems = [];
        this.inspection.sections.forEach((p: any) => (p.selected = false));
      });
  }

  public gotoUpdateStatusPage() {
    return this.router.navigate([this.templatesRoute, this.inspection.id, 'status']);
  }

  public gotoInformationPage() {
    return this.router.navigate([
      this.templatesRoute,
      this.inspection.id,
      'view',
      this.templatesRoute + '/' + this.inspection.id
    ]);
  }

  public gotoAssociatesMachinePage() {
    return this.router.navigate([this.templatesRoute, this.inspection.id, 'categories', 'edit']);
  }

  public gotoEditPage() {
    return this.router.navigate([this.templatesRoute, this.inspection.id, 'edit']);
  }

  public gotoAddSectionPage() {
    return this.router.navigate([this.templatesRoute, this.inspection.id, 'addsection']);
  }

  public gotoCreateSectionPage() {
    return this.router.navigate([this.templatesRoute, this.inspection.id, 'sections', 'new']);
  }

  public gotoAddPluginPage() {
    return this.router.navigate([
      this.templatesRoute,
      this.inspection.id,
      'addPlugin',
      this.inspection.applicationSubType || ''
    ]);
  }

  public openFileSelector() {
    this.fileInput.nativeElement.click();
  }

  public openTranslationSelector() {
    this.translationInput.nativeElement.click();
  }

  public upload(e: any) {
    const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    if (files && files.length > 0) {
      const inputFile = new FormData();
      for (let i = 0; i < files.length; i++) {
        inputFile.append('files[' + i + ']', files[i], files[i].name);
      }

      this.inspectionModelService.uploadParts(this.inspectionId, inputFile).subscribe((data) => {
        // this.goToBack();
      });
    }
  }

  public uploadPartPrices(e: any) {
    const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    if (files && files.length > 0) {
      const inputFile = new FormData();
      for (let i = 0; i < files.length; i++) {
        inputFile.append(`files[${i}]`, files[i], files[i].name);
      }

      this.inspectionModelService.uploadPartsPrices(this.inspectionId, inputFile).subscribe((data) => {
        // this.goToBack();
      });
    }
  }

  uploadTranslation(e: any) {
    const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    if (files.length > 0) {
      this.loading = true;
      const inputFile = new FormData();
      inputFile.append('files[0]', files[0], files[0].name);
      this.language.uploadTranslationFiles(inputFile).subscribe((data) => {
        this.loading = false;
      });
    }
  }

  downloadTranslation() {
    this.language.downloadTranslationFiles([`inspection_${this.inspectionId}`]);
  }

  openDeleteConfirm() {
    this.deleteConfirm.open();
  }

  deleteInspection(e: any) {
    this.inspectionModelService.deleteInspection(this.inspectionId).subscribe((success: boolean) => {
      if (success) {
        this.goToBack();
      }
    });
  }

  public openOrderSectionsPage() {
    const items = _.map(this.inspection.sections, (el: InspectionSection) => ({
      id: el.id,
      name: el.name,
      type: 'section',
      order: el.order
    }));

    this.orderSectionsModal.open(items);
  }

  saveSectionsOrder(items: any) {
    const orderableItems = _.map(items, (item: any) => ({
      id: item.id,
      order: item.order
    }));

    this.inspectionModelService.orderSections(this.inspectionId, orderableItems).subscribe((el) => { });
  }

  public openOrderPluginsPage() {
    const orderableItems = _.map(this.inspection.plugins, (p: any) => ({
      id: p.id,
      name: this.language.getInstantValue(`plugins.${p.pluginType}.name`) || p.name || p.pluginType,
      type: 'plugin',
      order: p.order
    }));

    this.orderPluginsModal.open(orderableItems);
  }

  public getPluginNameByPluginType(pluginType: string): string {
    const data = _.find(this.plugins, function (p: any) {
      return p.PluginType === pluginType;
    });
    if (data) {
      return data.Name;
    } else {
      return pluginType;
    }
  }

  savePluginsOrder(items: any) {
    const orderableItems = _.map(items, (item: any) => ({
      id: item.id,
      order: item.order
    }));

    this.inspectionModelService.orderPlugins(this.inspectionId, orderableItems).subscribe((el) => { });
  }

  goToBack(): void {
    if (this.parentTemplateId) {
      this.router.navigate([this.templatesRoute, this.parentTemplateId, 'templates']);
      return;
    }
    if (this.inspection.applicationType === ApplicationType.Installation) {
      this.router.navigate([this.templatesRoute, 'children']);
      return;
    }
    this.router.navigate([this.templatesRoute]);
  }

  refreshPage() {
    this.refresh();
  }
}
